/**
 * Client-side navigation data decompression utility
 * Decompresses wire format back to NavItem structure
 */

import type { CloudinaryImageType } from '../types/Image';
import type {
  CompressedNavigationData,
  CompressedNavItem,
  CompressedUrl,
  CompressedText,
  CompressedImage,
  NavItemRuntime,
  NavItemWithChildrenRuntime,
} from '../types/CompressedNav';

export class NavDataDecompressor {
  private idMap: Map<number, string>;
  private urlDict: string[];
  private imageUrlPrefixes: string[];
  private stringDict: string[];
  private items: CompressedNavItem[];
  private topLevel: string[] | number[];
  private topLevelTests?: string[];
  private useNumericIds: boolean;

  constructor(compressed: CompressedNavigationData) {
    this.useNumericIds = compressed.numericIds || false;
    this.idMap = compressed.m ? new Map(compressed.m) : new Map();
    this.urlDict = compressed.u;
    this.imageUrlPrefixes = compressed.i;
    this.stringDict = compressed.s;
    this.items = compressed.d;
    this.topLevel = compressed.t;
    this.topLevelTests = compressed.tt;
  }

  /**
   * Decompress URL
   */
  private decompressUrl(urlData: string | CompressedUrl | null | undefined): string {
    if (!urlData) return '';
    if (typeof urlData === 'string') return urlData;

    if (Array.isArray(urlData)) {
      const [baseIdx, pathData] = urlData;
      const base = this.urlDict[baseIdx];
      const path = typeof pathData === 'number' ? this.urlDict[pathData] : pathData;
      return base + path;
    }

    return '';
  }

  /**
   * Decompress image URL
   */
  private decompressImageUrl(urlData: string | CompressedUrl | undefined): string {
    if (!urlData) return '';
    if (typeof urlData === 'string') return urlData;

    if (Array.isArray(urlData)) {
      const [prefixIdx, remaining] = urlData;
      const prefix = this.imageUrlPrefixes[prefixIdx];
      return prefix + remaining;
    }

    return '';
  }

  /**
   * Decompress text
   */
  private decompressText(textData: string | CompressedText | undefined): string {
    if (!textData) return '';
    if (typeof textData === 'string') return textData;

    if (Array.isArray(textData)) {
      return textData
        .map((part) => {
          if (Array.isArray(part) && part[0] === '$') {
            return this.stringDict[part[1]];
          }
          return part;
        })
        .join('');
    }

    return '';
  }

  /**
   * Decompress image object
   */
  private decompressImage(imageData: CompressedImage | null | undefined): CloudinaryImageType {
    if (!imageData) return {} as CloudinaryImageType;

    const image: Partial<CloudinaryImageType> = {};

    if (imageData.u) {
      image.url = this.decompressImageUrl(imageData.u);
    }

    if (imageData.a) {
      image.alt = imageData.a;
    }

    if (imageData.w) {
      image.width = imageData.w;
    }

    if (imageData.h) {
      image.height = imageData.h;
    }

    return image as CloudinaryImageType;
  }

  /**
   * Decode single item
   */
  private decodeItem(encoded: CompressedNavItem): NavItemRuntime {
    const [
      idx,
      text,
      url,
      analyticsId,
      isVisual = false,
      startOfMx = false,
      pushToNextColumn = false,
      order = null,
      children = null,
      theme = null,
      imageData = null,
      seeAllMinimal = null,
      seeAllCategory = null,
      description = null,
      badge = null,
      abTestSelection = null,
    ] = encoded;

    // Use numeric index as ID if in numeric mode, otherwise lookup from map
    const id = this.useNumericIds ? String(idx) : (this.idMap.get(idx) || '');

    // Base properties - use runtime types that reflect actual data structure
    const baseItem: Partial<NavItemRuntime> = {
      id,
      theme: theme || '',
      text: this.decompressText(text),
      url: this.decompressUrl(url),
      analyticsId: analyticsId || '',
      isVisual,
      image: this.decompressImage(imageData),
      startOfMx,
      pushToNextColumn,
    };

    if (order !== null) {
      baseItem.order = order;
    }

    // If has children property (even if empty), add children array
    if (children !== null) {
      (baseItem as Partial<NavItemWithChildrenRuntime>).children = []; // Will be populated in second pass if not empty
    }

    // Add extended fields if they exist
    if (seeAllMinimal) baseItem.seeAllMinimal = seeAllMinimal;
    if (seeAllCategory) baseItem.seeAllCategory = seeAllCategory;
    if (description) baseItem.description = description;
    if (badge) baseItem.badge = badge;
    if (abTestSelection) baseItem.abTestSelection = abTestSelection;

    return baseItem as NavItemRuntime;
  }

  /**
   * Main decompression function
   */
  decompress(): {
    navigation: Record<string, NavItemRuntime>;
    topLevelNavNodes: string[];
    topLevelNavNodeTests?: string[];
  } {
    const navigation: Record<string, NavItemRuntime> = {};
    const itemMap = new Map<string, NavItemRuntime>();

    // First pass: decode all items
    this.items.forEach((encoded) => {
      const item = this.decodeItem(encoded);
      navigation[item.id] = item;
      itemMap.set(item.id, item);
    });

    // Second pass: resolve children references
    // Note: VistaPrint's actual data structure uses string IDs for children,
    // not full NavItem objects (despite what the TypeScript types suggest)
    this.items.forEach((encoded) => {
      const [idx, , , , , , , , children] = encoded;
      const id = this.useNumericIds ? String(idx) : (this.idMap.get(idx) || '');
      const item = navigation[id];

      if ('children' in item && children && children.length > 0) {
        // Convert indices to string IDs (either numeric or UUID)
        const resolvedChildren = children
          .map((childIdx) => this.useNumericIds ? String(childIdx) : this.idMap.get(childIdx))
          .filter((childId): childId is string => childId !== undefined);

        // Now properly typed with NavItemWithChildrenRuntime
        (item as NavItemWithChildrenRuntime).children = resolvedChildren;
      }
    });

    // Convert top level indices to IDs
    const topLevelIds = this.topLevel.map((idOrIdx): string => {
      if (typeof idOrIdx === 'number') {
        return this.useNumericIds ? String(idOrIdx) : this.idMap.get(idOrIdx) || '';
      }
      return idOrIdx;
    });

    return {
      navigation,
      topLevelNavNodes: topLevelIds,
      topLevelNavNodeTests: this.topLevelTests,
    };
  }
}

/**
 * Convenience function for decompressing navigation data
 */
export function decompressNavigationData(compressed: CompressedNavigationData): {
  navigation: Record<string, NavItemRuntime>;
  topLevelNavNodes: string[];
  topLevelNavNodeTests?: string[];
} {
  const decompressor = new NavDataDecompressor(compressed);
  return decompressor.decompress();
}

/**
 * Check if data is in compressed format
 */
export function isCompressedFormat(data: unknown): data is CompressedNavigationData {
  if (typeof data !== 'object' || data === null) return false;
  const obj = data as Record<string, unknown>;
  return obj.v === 2;
}
